import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel } from 'react-responsive-carousel';

import grayCurveTop from 'public/static/images/gray-curve-top.png';
import grayCurveBottom from 'public/static/images/gray-curve-bottom.png';
import rightArrow from 'public/static/images/right-arrow-red-small.png';
import baseball from 'public/static/images/engineering/hero/baseball.jpg';
import cto from 'public/static/images/engineering/hero/cto.jpg';
import escapeRoom from 'public/static/images/engineering/hero/escaperoom.jpg';
import teams from 'public/static/images/engineering/principles/teams.jpg';
import impact from 'public/static/images/engineering/principles/impact.jpg';
import thoughtful from 'public/static/images/engineering/principles/thoughtful.jpg';
import leftQuote from 'public/static/images/engineering/learnings/left-quote.png';
import rightQuote from 'public/static/images/engineering/learnings/right-quote.png';
import elasticsearch from 'public/static/images/engineering/technology/elasticsearch.png';
import redshift from 'public/static/images/engineering/technology/redshift.png';
import lambda from 'public/static/images/engineering/technology/lambda.png';
import kinesis from 'public/static/images/engineering/technology/kinesis.png';
import firebase from 'public/static/images/engineering/technology/firebase.png';
import react from 'public/static/images/engineering/technology/react.png';
import redux from 'public/static/images/engineering/technology/redux.png';
import node from 'public/static/images/engineering/technology/node.png';
import tensorFlow from 'public/static/images/engineering/technology/tensorflow.png';
import kotlin from 'public/static/images/engineering/technology/kotlin.png';
import swift from 'public/static/images/engineering/technology/swift.png';
import heroku from 'public/static/images/engineering/technology/heroku.png';
import rails from 'public/static/images/engineering/technology/rails.png';
import python from 'public/static/images/engineering/technology/python.png';
import spark from 'public/static/images/engineering/technology/spark.png';
import nextjs from 'public/static/images/engineering/technology/nextjs.png';
import ai from 'public/static/images/engineering/challenges/ai.svg';
import scale from 'public/static/images/engineering/challenges/scale.svg';
import security from 'public/static/images/engineering/challenges/security.svg';
import experience from 'public/static/images/engineering/challenges/experience.svg';
import prevArrow from 'public/static/images/engineering/learnings/prev-arrow.svg';
import nextArrow from 'public/static/images/engineering/learnings/next-arrow.svg';
import codeBg from 'public/static/images/engineering/hero/code-bg.jpg';

import { screen, colors } from './styles/variables';

export default function Engineering(): JSX.Element {
  return (
    <div className="wrapper">
      <div className="engineering">
        <Cover />
        <Overview />
        <Principles />
        <Challenges />
        <div className="container">
          <div className="row row--small-gutter">
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
              <Learnings />
            </div>
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
              <Technologies />
            </div>
          </div>
        </div>
        <LearnMore />
      </div>
      <style jsx>
        {`
          .wrapper {
            position: relative;
          }

          .engineering {
            font-size: 19px;
            line-height: 25px;
            font-weight: 300;
          }
        `}
      </style>
    </div>
  );
}

function Cover(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <div className="cover" />
      <div className="gray-curve" />
      <div className="hero">
        <div className="container">
          <div className="row row--small-gutters">
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
              <h1 className="engineering-title">{t('engineering.title')}</h1>
              <div className="subtitle">{t('engineering.subtitle')}</div>
              <ul className="engineering-cover-list hidden-xs hidden-sm">
                <li className="engineering-cover-list-item">
                  <a className="engineering-cover-list-item-anchor" href="#overview">
                    {t('engineering.nav.overview')}
                    <div className="nav-arrow" />
                  </a>
                </li>
                <li className="engineering-cover-list-item">
                  <a className="engineering-cover-list-item-anchor" href="#principles">
                    {t('engineering.nav.principles')}
                    <div className="nav-arrow" />
                  </a>
                </li>
                <li className="engineering-cover-list-item">
                  <a className="engineering-cover-list-item-anchor" href="#technologies">
                    {t('engineering.nav.technologies')}
                    <div className="nav-arrow" />
                  </a>
                </li>
              </ul>
            </div>
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
              <MainCarousel />
            </div>
            <div className="col-xs-12 col-sm-12 hidden-md hidden-lg">
              <ul className="engineering-cover-list hidden-xs hidden-sm">
                <li className="engineering-cover-list-item">
                  <a className="engineering-cover-list-item-anchor" href="#overview">
                    {t('engineering.nav.overview')}
                    <div className="nav-arrow" />
                  </a>
                </li>
                <li className="engineering-cover-list-item">
                  <a className="engineering-cover-list-item-anchor" href="#principles">
                    {t('engineering.nav.principles')}
                    <div className="nav-arrow" />
                  </a>
                </li>
                <li className="engineering-cover-list-item">
                  <a className="engineering-cover-list-item-anchor" href="#technologies">
                    {t('engineering.nav.technologies')}
                    <div className="nav-arrow" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="gray-curve gray-curve--relative hidden-md hidden-lg" />
      <style jsx>
        {`
          .cover {
            position: absolute;
            background-image: url('${codeBg}');
            width: 100%;
            height: 360px;
            top: 61px;
          }

          .hero {
            position: relative;
            width: 100%;
            z-index: 10;
            padding-top: 50px;
          }

          @media (min-width: ${screen.mdMin}) {
            .hero {
              height: 400px;
            }
          }

          .gray-curve {
            position: absolute;
            width: 100%;
            height: 50px;
            background-size: cover;
            background-position: center center;
            margin-top: -20px;
            background-image: url(${grayCurveTop});
            top: 375px;
            margin: 0;
            z-index: 0;
          }

          .gray-curve.gray-curve--relative {
            position: relative;
            top: 0;
          }

          .engineering-title {
            margin: 0;
            padding: 0;
            font-size: 43px;
            font-weight: bold;
            line-height: 40px;
            margin-bottom: 15px;
          }

          .subtitle {
            margin-bottom: 15px;
          }

          .engineering-cover-list {
            margin: 0;
            padding: 0;
          }

          .engineering-cover-list-item {
            list-style-type: none;
            display: block;
            padding: 0 0 10px 0;
          }

          .engineering-cover-list-item:last-child {
            padding: 0;
          }

          @media (min-width: ${screen.mdMin}) {
            .engineering-cover-list-item {
              display: inline-block;
              padding: 0 20px 0 0;
            }
          }

          .engineering-cover-list-item-anchor {
            display: inline-block;
            color: ${colors.ALR1};
            font-size: 18px;
            text-decoration: none;
          }

          .nav-arrow {
            position: relative;
            display: inline-block;
            top: 1px;
            margin-left: 3px;
            height: 12px;
            width: 9px;
            background: url(${rightArrow});
            background-size: 9px 12px;
          }
        `}
      </style>
      <style jsx global>
        {`
          // TODO: find a better way to import  vendor styles

          .carousel .control-arrow,
          .carousel.carousel-slider .control-arrow {
            transition: all 0.25s ease-in;
            opacity: 0.4;
            filter: alpha(opacity=40);
            position: absolute;
            z-index: 2;
            top: 20px;
            background: none;
            border: 0;
            font-size: 32px;
            cursor: pointer;
          }
          .carousel .control-arrow:hover {
            opacity: 1;
            filter: alpha(opacity=100);
          }
          .carousel .control-arrow:before,
          .carousel.carousel-slider .control-arrow:before {
            margin: 0 5px;
            display: inline-block;
            border-top: 8px solid transparent;
            border-bottom: 8px solid transparent;
            content: '';
          }
          .carousel .control-disabled.control-arrow {
            opacity: 0;
            filter: alpha(opacity=0);
            cursor: inherit;
            display: none;
          }
          .carousel .control-prev.control-arrow {
            left: 0;
          }
          .carousel .control-prev.control-arrow:before {
            border-right: 8px solid #fff;
          }
          .carousel .control-next.control-arrow {
            right: 0;
          }
          .carousel .control-next.control-arrow:before {
            border-left: 8px solid #fff;
          }

          .carousel {
            position: relative;
            width: 100%;
          }
          .carousel * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          .carousel button {
            outline: 0;
            border: 0;
            background: none;
          }
          .carousel img {
            width: 100%;
            display: inline-block;
            pointer-events: none;
          }
          .carousel .carousel {
            position: relative;
          }
          .carousel .control-arrow {
            top: 50%;
            margin-top: -13px;
            font-size: 18px;
          }
          .carousel .thumbs-wrapper {
            margin: 20px;
            overflow: hidden;
          }
          .carousel .thumbs {
            transition: all 0.15s ease-in;
            transform: translate3d(0, 0, 0);
            position: relative;
            list-style: none;
            white-space: nowrap;
          }
          .carousel .thumb {
            transition: border 0.15s ease-in;
            display: inline-block;
            width: 80px;
            margin-right: 6px;
            white-space: nowrap;
            overflow: hidden;
            border: 3px solid #fff;
            padding: 2px;
          }
          .carousel .thumb.selected,
          .carousel .thumb:hover {
            border: 3px solid #333;
            padding: 2px;
          }
          .carousel .thumb img {
            vertical-align: top;
          }
          .carousel.carousel-slider {
            position: relative;
            margin: 0;
            overflow: hidden;
          }
          .carousel.carousel-slider .control-arrow {
            top: 0;
            color: #fff;
            font-size: 26px;
            bottom: 0;
            margin-top: 0;
            padding: 5px;
          }
          .carousel.carousel-slider .control-arrow:hover {
            background: rgba(0, 0, 0, 0.2);
          }
          .carousel .slider-wrapper {
            overflow: hidden;
            margin: auto;
            width: 100%;
            transition: height 0.15s ease-in;
          }
          .carousel .slider-wrapper.axis-horizontal .slider {
            -ms-box-orient: horizontal;
            display: flex;
          }
          .carousel .slider-wrapper.axis-horizontal .slider .slide {
            flex-direction: column;
            flex-flow: column;
          }
          .carousel .slider-wrapper.axis-vertical {
            -ms-box-orient: horizontal;
            display: flex;
          }
          .carousel .slider-wrapper.axis-vertical .slider {
            flex-direction: column;
          }
          .carousel .slider {
            position: relative;
            list-style: none;
            width: 100%;
          }
          .carousel .slider.animated {
            transition: all 0.35s ease-in-out;
          }
          .carousel .slide {
            min-width: 100%;
            margin: 0;
            position: relative;
            text-align: center;
            background: #000;
          }
          .carousel .slide img {
            width: 100%;
            vertical-align: top;
            border: 0;
          }
          .carousel .slide iframe {
            display: inline-block;
            width: calc(100% - 80px);
            margin: 0 40px 40px;
            border: 0;
          }
          .carousel .slide .legend {
            transition: all 0.5s ease-in-out;
            position: absolute;
            bottom: 40px;
            left: 50%;
            margin-left: -45%;
            width: 90%;
            border-radius: 10px;
            background: #000;
            color: #fff;
            padding: 10px;
            font-size: 12px;
            text-align: center;
            opacity: 0.25;
            transition: opacity 0.35s ease-in-out;
          }
          .carousel .control-dots {
            position: absolute;
            bottom: 0;
            margin: 10px 0;
            text-align: center;
            width: 100%;
          }
          @media (min-width: 960px) {
            .carousel .control-dots {
              bottom: 0;
            }
          }
          .carousel .control-dots .dot {
            transition: opacity 0.25s ease-in;
            opacity: 0.3;
            filter: alpha(opacity=30);
            box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.9);
            background: #fff;
            border-radius: 50%;
            width: 8px;
            height: 8px;
            cursor: pointer;
            display: inline-block;
            margin: 0 8px;
          }
          .carousel .control-dots .dot.selected,
          .carousel .control-dots .dot:hover {
            opacity: 1;
            filter: alpha(opacity=100);
          }
          .carousel .carousel-status {
            position: absolute;
            top: 0;
            right: 0;
            padding: 5px;
            font-size: 10px;
            text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.9);
            color: #fff;
          }
          .carousel:hover .slide .legend {
            opacity: 1;
          }
        `}
      </style>
    </div>
  );
}

function MainCarousel(): JSX.Element {
  const settings = {
    infiniteLoop: true,
    autoPlay: true,
    interval: 3000,
    dynamicHeight: true,
    showIndicators: true,
    showStatus: false,
    showThumbs: false,
    stopOnHover: true,
    centerMode: true,
    centerSlidePercentage: 100,
  };

  return (
    <div className="carousel-container">
      <Carousel {...settings}>
        <div>
          <img src={baseball} />
        </div>
        <div>
          <img src={cto} />
        </div>
        <div>
          <img src={escapeRoom} />
        </div>
      </Carousel>
      <style jsx>
        {`
          .carousel-container {
            width: 100%;
            box-shadow: 0 0 10px 0 #999;
            margin-bottom: 25px;
          }

          @media (min-width: ${screen.mdMin}) and (max-width: ${screen.mdMax}) {
            .carousel-container {
              width: 373px;
            }
          }

          @media (min-width: ${screen.lgMin}) {
            .carousel-container {
              width: 455px;
            }
          }
        `}
      </style>
    </div>
  );
}

function Overview(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="container">
      <div className="row row--small-gutters">
        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
          <a id="overview" />
          <h2 className="engineering-overview-title">{t('engineering.overview.title')}</h2>
          <div>{t('engineering.overview.p1')}</div>
          <br />
          <div>{t('engineering.overview.p2opener')}</div>
          <ul className="engineering-overview-list">
            <li>{t('engineering.overview.p2bullet1')}</li>
            <li>{t('engineering.overview.p2bullet2')}</li>
            <li>{t('engineering.overview.p2bullet3')}</li>
            <li>{t('engineering.overview.p2bullet4')}</li>
            <li>{t('engineering.overview.p2bullet5')}</li>
          </ul>
          <div
            dangerouslySetInnerHTML={{
              __html: t('engineering.overview.p3', {
                interpolation: { escapeValue: false },
              }),
            }}
          />
        </div>
      </div>
      <style jsx>
        {`
          .container {
            margin-bottom: 50px;
          }

          .engineering-overview-title {
            margin: 0;
            padding: 0;
            font-size: 33px;
            font-weight: bold;
            line-height: 25px;
            margin: 15px 0 15px 0;
          }

          @media (min-width: ${screen.smMin}) and (max-width: ${screen.smMax}) {
            .engineering-overview-title {
              margin: 0;
              padding: 0;
              font-size: 33px;
              font-weight: bold;
              line-height: 25px;
              margin: 15px 0 25px 0;
            }
          }

          @media (min-width: ${screen.mdMin}) {
            .engineering-overview-title {
              margin: 0;
              padding: 0;
              font-size: 33px;
              font-weight: bold;
              line-height: 25px;
              margin: 0 0 25px 0;
            }
          }

          .engineering-overview-list {
            list-style: none;
            margin-left: 0;
            padding-left: 1em;
          }

          .engineering-overview-list > li:before {
            display: inline-block;
            content: '-';
            width: 1em;
            margin-left: -1em;
          }
        `}
      </style>
    </div>
  );
}

function Principles(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="container">
      <div className="row row--small-gutters-gtxs row--no-gutters-xs">
        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
          <a id="principles" />
          <h2 className="h2 title">{t('engineering.principles.title')}</h2>
        </div>
      </div>
      <div className="row row--small-gutters-gtxs row--no-gutters-xs">
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
          <img className="principles-image" src={teams} />
        </div>
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 align-self-center">
          <h2 className="h2 title">{t('engineering.principles.strongTeamsTitle')}</h2>
          <div className="description">{t('engineering.principles.strongTeams')}</div>
        </div>
      </div>
      <div className="row row--small-gutters-gtxs row--no-gutters-xs">
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
          <img className="principles-image" src={impact} />
        </div>
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 align-self-center">
          <h2 className="h2 title">{t('engineering.principles.impactTitle')}</h2>
          <div className="description">{t('engineering.principles.impact')}</div>
        </div>
      </div>
      <div className="row row--small-gutters-gtxs row--no-gutters-xs">
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
          <img className="principles-image" src={thoughtful} />
        </div>
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 align-self-center">
          <h2 className="h2 title">{t('engineering.principles.thoughtfulTitle')}</h2>
          <div className="description">{t('engineering.principles.thoughtful')}</div>
        </div>
      </div>
      <style jsx>
        {`
          .container {
            margin-top: 50px;
          }

          @media (min-width: ${screen.smMin}) {
            .container {
              margin-top: 25px;
            }
          }

          .principles-image {
            box-shadow: 0 0 10px 0 #999;
            width: 100vw;
            margin: 0 0 10px 0;
          }

          @media (min-width: ${screen.smMin}) {
            .principles-image {
              width: 100%;
              margin: 0 0 20px 0;
            }
          }

          .title {
            margin: 0;
            padding: 0;
            font-size: 33px;
            font-weight: bold;
            line-height: 25px;
            margin: 30px 15px 0 15px;
          }

          @media (min-width: ${screen.smMin}) {
            .title {
              margin: 0;
              padding: 0;
              font-size: 33px;
              font-weight: bold;
              line-height: 25px;
              margin-left: 0;
              margin-right: 0;
            }
          }

          @media (max-width: ${screen.smMax}) {
            .title {
              margin: 0;
              padding: 0;
              font-size: 33px;
              font-weight: bold;
              line-height: 25px;
              margin-top: 10px;
              margin-bottom: 20px;
            }
          }

          @media (min-width: ${screen.mdMin}) {
            .title {
              margin: 0;
              padding: 0;
              font-size: 33px;
              font-weight: bold;
              line-height: 25px;
              margin: 30px 0;
            }
          }

          .description {
            margin-top: 15px;
            margin-bottom: 15px;
            margin-left: 15px;
            margin-right: 15px;
          }

          @media (min-width: ${screen.smMin}) {
            .description {
              margin-left: 0;
              margin-right: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

function Challenges(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <div className="container">
        <div className="row row--small-gutters">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
            <h2 id="challenges" className="h2 header">
              {t('engineering.challenges.title')}
            </h2>
            <div className="row row--small-gutters">
              <div className="ai challenge col-xs-12 col-sm-12 col-md-6 col-lg-6">
                <img className="challenges-image" src={ai} />
                <h2 className="h2 title">{t('engineering.challenges.aiTitle')}</h2>
                <div className="description">{t('engineering.challenges.ai')}</div>
              </div>
              <div className="scale challenge col-xs-12 col-sm-12 col-md-6 col-lg-6">
                <img className="challenges-image" src={scale} />
                <h2 className="h2 title">{t('engineering.challenges.scaleTitle')}</h2>
                <div className="description">{t('engineering.challenges.scale')}</div>
              </div>
            </div>
            <div className="row row--small-gutters">
              <div className="security challenge col-xs-12 col-sm-12 col-md-6 col-lg-6">
                <img className="challenges-image" src={security} />
                <h2 className="h2 title">{t('engineering.challenges.securityTitle')}</h2>
                <div className="description">{t('engineering.challenges.security')}</div>
              </div>
              <div className="experience challenge col-xs-12 col-sm-12 col-md-6 col-lg-6">
                <img className="challenges-image" src={experience} />
                <h2 className="h2 title">{t('engineering.challenges.experienceTitle')}</h2>
                <div className="description">{t('engineering.challenges.experience')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="gray-curve" />
      <style jsx>
        {`
          .header {
            margin: 50px 0 15px 0;
          }

          @media (min-width: ${screen.mdMin}) {
            .header {
              margin: 35px 0 25px 0;
            }
          }

          .challenge {
            height: 400px;
          }

          @media (min-width: ${screen.mdMin}) {
            .description {
              padding: 0 40px;
            }
          }

          .challenges-image {
            display: block;
            margin: 0 auto;
            width: 220px;
            height: 120px;
            object-fit: contain;
          }

          .title {
            text-align: center;
            margin-top: 20px;
            margin-bottom: 15px;
          }

          .ai {
            padding-top: 35px;
            padding-bottom: 20px;
          }

          @media (min-width: ${screen.mdMin}) {
            .ai {
              border-right: 1px solid ${colors.G2};
              border-bottom: 1px solid ${colors.G2};
            }
          }

          .ai .challenges-image {
            height: 135px;
          }

          .scale {
            padding-top: 50px;
            padding-bottom: 20px;
          }

          @media (min-width: ${screen.mdMin}) {
            .scale {
              border-bottom: 1px solid ${colors.G2};
            }
          }

          .scale .challenges-image {
            height: 120px;
          }

          .security {
            padding-top: 35px;
            padding-bottom: 20px;
          }

          @media (min-width: ${screen.mdMin}) {
            .security {
              border-right: 1px solid ${colors.G2};
            }
          }

          .security .challenges-image {
            height: 135px;
          }

          .experience {
            padding-top: 50px;
            padding-bottom: 20px;
          }

          .gray-curve {
            position: relative;
            width: 100%;
            height: 50px;
            background-size: cover;
            background-position: center center;
            background-image: url(${grayCurveBottom});
            margin: 75px 0 75px 0;
            z-index: 0;
          }
        `}
      </style>
    </div>
  );
}

function Learnings(): JSX.Element {
  const { t } = useTranslation();
  const carousel = useRef<Carousel | null>();
  const carouselSettings = {
    infiniteLoop: true,
    autoPlay: true,
    interval: 5000,
    dynamicHeight: true,
    showIndicators: false,
    showStatus: false,
    showThumbs: false,
    stopOnHover: true,
    showArrows: false,
  };

  return (
    <div id="learnings">
      <h2 className="h2">{t('engineering.learnings.title')}</h2>
      <div className="examples">
        <Carousel
          ref={(carouselInstance): void => {
            carousel.current = carouselInstance;
          }}
          {...carouselSettings}
        >
          <div className="carousel-learnings-slide">
            <p>{t('engineering.learnings.examples.1')}</p>
          </div>
          <div className="carousel-learnings-slide">
            <p>{t('engineering.learnings.examples.2')}</p>
          </div>
          <div className="carousel-learnings-slide">
            <p>{t('engineering.learnings.examples.3')}</p>
          </div>
          <div className="carousel-learnings-slide">
            <p>{t('engineering.learnings.examples.4')}</p>
          </div>
        </Carousel>
      </div>
      <div className="control-container">
        <div className="controls">
          <img
            className="prev-learning"
            src={prevArrow}
            onClick={(): void => {
              if (carousel.current) {
                carousel.current.decrement();
              }
            }}
          />
          <img
            className="next-learning"
            src={nextArrow}
            onClick={(): void => {
              if (carousel.current) {
                carousel.current.increment();
              }
            }}
          />
        </div>
      </div>
      <div className="description">
        <h3 className="learnings-at-autolist-subtitle">{t('engineering.learnings.atAutolist')}</h3>
        <div>{t('engineering.learnings.description')}</div>
      </div>
      <style jsx>
        {`
          .examples {
            height: auto;
          }

          .control-container {
            position: relative;
            margin: 25px 0 40px 0;
          }

          .controls {
            width: 120px;
            margin: 0 auto;
          }

          .next-learning {
            margin-left: 20px;
          }

          .prev-learning,
          .next-learning {
            height: 50px;
            width: 50px;
            cursor: pointer;
          }

          .learnings-at-autolist-subtitle {
            margin: 20px 0;
            font-size: 22px;
            font-weight: 600;
          }

          .description {
            border-top: 1px solid #cccccc;
            margin-bottom: 75px;
          }
        `}
      </style>
      <style jsx global>
        {`
          #learnings .slider-wrapper {
            background: url(${leftQuote}), url(${rightQuote});
            background-position: left top, right bottom;
            background-repeat: no-repeat, no-repeat;
            background-size: 35%, 35%;
          }

          #learnings .slide {
            text-align: left;
            background: transparent;
            margin-top: 20px;
            margin-bottom: 30px;
            line-height: 1.4;
          }

          #learnings .slide .carousel-learnings-slide {
            vertical-align: middle;
            position: relative;
            height: 290px;
          }

          #learnings .slide .carousel-learnings-slide p {
            margin: 0;
            position: absolute;
            top: 50%;
            transform: translate(0, -50%);
            font-size: 19px;
          }

          @media (min-width: ${screen.smMin}) {
            #learnings {
              font-size: 26px;
            }
          }
        `}
      </style>
    </div>
  );
}

function Technologies(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <div className="hspacer-15px visible-xs visible-sm" />
      <h2 id="technologies" className="technologies-title">
        {t('engineering.technologies.title')}
      </h2>
      <div className="row row--small-gutters">
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={elasticsearch} />
          </div>
          <div className="label">Elasticsearch</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={redshift} />
          </div>
          <div className="label">Amazon Redshift</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={lambda} />
          </div>
          <div className="label">Amazon Lambda</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={kinesis} />
          </div>
          <div className="label">Amazon Kinesis</div>
        </div>
      </div>
      <div className="row row--small-gutters">
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={react} />
          </div>
          <div className="label">React</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={redux} />
          </div>
          <div className="label">Redux</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={node} />
          </div>
          <div className="label">Node</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={nextjs} />
          </div>
          <div className="label">Next.js</div>
        </div>
      </div>
      <div className="row row--small-gutters">
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={firebase} />
          </div>
          <div className="label">Firebase</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={kotlin} />
          </div>
          <div className="label">Kotlin</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={swift} />
          </div>
          <div className="label">Swift</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={heroku} />
          </div>
          <div className="label">Heroku</div>
        </div>
      </div>
      <div className="row row--small-gutters">
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={python} />
          </div>
          <div className="label">Python</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={rails} />
          </div>
          <div className="label">Ruby on Rails</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={tensorFlow} />
          </div>
          <div className="label">TensorFlow</div>
        </div>
        <div className="tech col-xs-3 col-sm-3 col-md-3 col-lg-3">
          <div className="logo">
            <img className="technologies-logo" src={spark} />
          </div>
          <div className="label">Spark</div>
        </div>
      </div>
      <style jsx>
        {`
          .technologies-title {
            margin: 0;
            padding: 0;
            font-size: 33px;
            font-weight: bold;
            line-height: 25px;
            margin: 0 0 15px 0;
          }

          .tech {
            margin-bottom: 20px;
            text-align: center;
            position: relative;
          }

          .logo {
            height: 120px;
            transform: translate(0, 0);
          }

          .label {
            width: 100%;
            font-size: 15px;
            line-height: 18px;
          }

          .technologies-logo {
            max-height: 60%;
            max-width: 60%;
            width: 100%;
            height: auto;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            margin: auto;
          }
        `}
      </style>
    </div>
  );
}

function LearnMore(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <hr />
      <div className="container">
        <div className="row row--small-gutters">
          <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
            <div className="hspacer-15px visible-xs visible-sm" />
            <h2 className="learn-more-title" id="cta">
              {t('engineering.cta.learnmore')}
            </h2>
            <div
              className="chat"
              dangerouslySetInnerHTML={{
                __html: t('engineering.cta.chat', {
                  interpolation: { escapeValue: false },
                }),
              }}
            />
          </div>

          <div className="cta col-xs-12 col-sm-12 col-md-6 col-lg-6">
            <a href="/about-us#jobs" className="AL-button AL-button--medium _center-block">
              {t('engineering.cta.viewjobs')}
            </a>
          </div>
        </div>
      </div>
      <hr />
      <style jsx>
        {`
          .container {
            padding-top: 30px;
          }

          .learn-more-title {
            margin-top: 30px;
            font-size: 36px;
          }

          @media (max-width: ${screen.xsMax}) {
            .learn-more-title {
              margin-bottom: 15px;
            }
          }

          @media (min-width: ${screen.smMin}) {
            .learn-more-title {
              margin-bottom: 25px;
            }
          }

          .chat {
            font-size: 19px;
            line-height: 25px !important;
            font-weight: 300;
          }

          .chat a {
            font-size: 19px;
          }

          .cta {
            height: 160px;
          }

          .cta a {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }

          hr {
            height: 30px;
            border-color: white;
            margin: 0;

            /* Permalink - use to edit and share this gradient: http://colorzilla.com/gradient-editor/#ffffff+0,cccccc+100&0.02+10,0.03+23,0.05+35,0.1+55,0.25+74,0.3+88,0.4+95,0.65+100 */

            background: linear-gradient(
              to bottom,
              rgba(255, 255, 255, 0.02) 0%,
              rgba(250, 250, 250, 0.02) 10%,
              rgba(243, 243, 243, 0.03) 23%,
              rgba(237, 237, 237, 0.05) 35%,
              rgba(227, 227, 227, 0.1) 55%,
              rgba(217, 217, 217, 0.25) 74%,
              rgba(210, 210, 210, 0.3) 88%,
              rgba(207, 207, 207, 0.4) 95%,
              rgba(204, 204, 204, 0.65) 100%
            ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
            filter: progid:DXImageTransform.Microsoft.gradient(
              startColorstr='#05ffffff',
              endColorstr='#a6cccccc',
              GradientType=0
            ); /* IE6-9 */
          }
        `}
      </style>
    </div>
  );
}
